import * as d3 from 'd3'
// @ts-ignore
import d3LocaleDE from 'd3-time-format/locale/de-DE'
// @ts-ignore
import d3LocaleEN from 'd3-time-format/locale/en-US'
import { TFunction } from 'i18next'
import createBasicChart from '../../components/D3/createBasicChart'
import createD3Component from '../../components/D3/D3'

import styles from './InsightsPage.module.scss'

const d3Locales: { [lng: string]: d3.TimeLocaleDefinition | undefined } = {
  en: d3LocaleEN,
  de: d3LocaleDE,
}

interface ChartData {
  fromDate: Date
  toDate: Date
  days: { when: Date; total: number }[]
}

function chart(svg: SVGSVGElement) {
  const $svg = d3.select(svg)

  // Area
  const $area = $svg.append('path').attr('class', styles.area).attr('fill', 'url(#areagradient)')

  const $gradient = $svg
    .append('defs')
    .append('linearGradient')
    .attr('id', 'areagradient')
    .attr('x1', 0)
    .attr('x2', 0)
    .attr('y1', 0)
    .attr('y2', '100%')
  $gradient.append('stop').attr('offset', '0%').attr('stop-color', 'var(--primary-light)')
  $gradient.append('stop').attr('offset', '100%').attr('stop-color', 'white')

  // Line
  const $line = $svg.append('path').attr('class', styles.line)

  // x Axis
  const $xAxis = $svg.append('g').attr('class', styles.xAxis)

  // y Axis
  const yScale = d3.scaleLinear()
  const yAxis = d3.axisLeft(yScale).ticks(6)
  const $yAxis = $svg.append('g').call(yAxis).attr('class', styles.yAxis)

  function update({ data, lng, t }: { data: ChartData; lng: string; t: TFunction }) {
    const { fromDate, toDate, days } = data

    const svgSize = $svg.node()!.getBoundingClientRect()
    const small = svgSize.width < 710
    const rect = {
      left: small ? 20 : 100,
      top: 20,
      width: svgSize.width - (small ? 40 : 120),
      height: svgSize.height - 80,
    }
    yAxis.tickPadding(small ? 0 : 20)

    // Update x axis
    // I don't see how to update the locale for an existing scale, so we have to
    // recreate it.
    d3.timeFormatDefaultLocale(d3Locales[lng] || d3LocaleEN)
    const xScale = d3.scaleTime()
    const xAxis = d3.axisBottom(xScale).tickPadding(20).ticks(Math.min(7, days.length))
    xScale.domain([fromDate, toDate]).range([rect.left, rect.left + rect.width])
    $xAxis.attr('transform', `translate(0, ${rect.top + rect.height})`).call(xAxis)

    // Update y axis
    const max = Math.max(60, ...days.map(({ total }) => total / 60))
    const magnitude = Math.pow(10, Math.floor(Math.log10(max)))
    yScale
      .domain([0, Math.ceil(max / magnitude) * magnitude])
      .range([rect.top + rect.height, rect.top])
      .tickFormat()
    yAxis.tickSize(-rect.width)
    yAxis.tickFormat((v) => t('format.{count} min', { count: Number(v) }))
    $yAxis.attr('transform', `translate(${rect.left}, 0)`).call(yAxis)
    $yAxis
      .selectAll('.tick text')
      .attr('transform', small ? 'translate(0, -10)' : 'none')
      .attr('text-anchor', small ? 'start' : 'end')

    // Update area
    $area.datum(days).attr(
      'd',
      d3
        .area<{ when: Date; total: number }>()
        .x((d) => xScale(d.when))
        .y0(yScale(0))
        .y1((d) => yScale(d.total / 60))
    )

    // Update line
    $line.datum(days).attr(
      'd',
      d3
        .line<{ when: Date; total: number }>()
        .x((d) => xScale(d.when))
        .y((d) => yScale(d.total / 60))
    )
  }

  return { update }
}

export default createD3Component(createBasicChart(chart), { className: styles.InsightsPageLearningInvestmentChart })
